import LogoutOutlined from '@mui/icons-material/LogoutOutlined'
import { AppBar, Avatar, Box, Button, Toolbar, Typography } from '@mui/material'
import { accountService } from '../../_services/accounts.service'

const logoImg = require('../../_assets/images/Coillte_Logo_Default.jpg')

/** Matches shadcn-vite-demo sidebar / EBS.VITE.AUCTION header */
const TOOLBAR_BG = 'hsl(222.2 47.4% 11.2%)'

function userInitials(email: string, name?: string): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase()
    }
    return (parts[0]?.slice(0, 2) ?? '?').toUpperCase()
  }
  return email
    .split('@')[0]
    .split(/[._-]/)
    .map(p => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('') || '?'
}

function userRoleLabel(): string {
  if (accountService.isAdmin) return 'Admin'
  if (accountService.isMonitor) return 'Monitor'
  if (accountService.isProxyBidder) return 'Proxy Bidder'
  return 'Bidder'
}

/** Props kept for API compatibility with `AppScreen` (sidebar toggle reserved). */
export const AppToolbar = (_: { toggleSidebar: () => void; isOpen: boolean }) => {
  const user = accountService.currentUserValue
  const email = user?.email ?? ''
  const name = user?.name as string | undefined
  const initials = userInitials(email, name)

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        left: 0,
        right: 0,
        backgroundColor: TOOLBAR_BG,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3 },
          gap: 2,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Brand: logo frame + titles */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
            }}
          >
            <Box
              component="img"
              src={logoImg}
              alt="Coillte"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', lineHeight: 1.1 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
              Coillte
            </Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
              Auction System
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: '1px',
            height: 20,
            mx: 0.5,
            bgcolor: 'rgba(255,255,255,0.2)',
          }}
        />

        <Typography
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.8)',
          }}
        >
          Live Auction
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#fff', maxWidth: 180 }}
              >
                {email}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                {userRoleLabel()}
              </Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            width: '1px',
            height: 20,
            mx: 0.5,
            bgcolor: 'rgba(255,255,255,0.2)',
            flexShrink: 0,
          }}
        />

        <Button
          color="inherit"
          onClick={() => accountService.logout()}
          title="Log out"
          startIcon={<LogoutOutlined sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.7)',
            minWidth: 'auto',
            px: 1.5,
            py: 0.75,
            flexShrink: 0,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Log out
          </Box>
        </Button>
      </Toolbar>
    </AppBar>
  )
}
